import type { SurveyAppearanceLogoAlignment } from "@/types/survey";
import { organizationDisplayName } from "@/lib/organization";
import { normalizeLogoSize } from "./surveyTheme";
import styles from "./SurveyBrand.module.css";

const ALIGNMENT_CLASS: Record<SurveyAppearanceLogoAlignment, string> = {
  left: styles.alignLeft,
  center: styles.alignCenter,
  right: styles.alignRight,
};

/** Minimal organization shape the shared brand chrome needs. Both the
 *  authenticated `Organization` model and the trimmed `PublicSurvey`
 *  organization payload satisfy this — keep the surface small so the
 *  editor and public page can share one component without coupling to
 *  either source's full type. */
export interface SurveyBrandOrganization {
  name?: string | null;
  nameAlias?: string | null;
  orgLogoUrl?: string | null;
  orgAddress?: string | null;
  orgCity?: string | null;
  orgState?: string | null;
  orgZip?: string | null;
  orgEmail?: string | null;
  orgPhone?: string | null;
}

interface SurveyBrandHeaderProps {
  /** Resolved logo URL — typically `getLogoUrl(survey, organization)`
   *  so survey-level overrides win over the org default. */
  logoUrl: string | null;
  organization: SurveyBrandOrganization | null | undefined;
  /** Horizontal alignment of the logo within the header. Defaults to
   *  centered to match historic behavior. */
  alignment?: SurveyAppearanceLogoAlignment;
  /** Logo max-height in px from the survey appearance setting. Clamped
   *  to the supported range; defaults to the historic logo size. */
  size?: number;
}

/** Header showing the survey/org logo, or the org name as a text
 *  fallback. Used at the top of the survey card on both the public
 *  landing page and the editor canvas preview. Logo alignment and size
 *  are driven by the survey appearance settings. */
export function SurveyBrandHeader({
  logoUrl,
  organization,
  alignment = "center",
  size,
}: SurveyBrandHeaderProps) {
  const orgName = organizationDisplayName(organization) || null;
  return (
    <header className={`${styles.header} ${ALIGNMENT_CLASS[alignment]}`}>
      {logoUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={logoUrl}
          alt={orgName ? `${orgName} logo` : "Logo"}
          className={styles.logo}
          style={{ maxHeight: `${normalizeLogoSize(size)}px` }}
        />
      ) : orgName ? (
        <span className={styles.logoText}>{orgName}</span>
      ) : null}
    </header>
  );
}

interface SurveyBrandFooterProps {
  organization: SurveyBrandOrganization | null | undefined;
}

/** Card footer with the org's contact summary plus the "Powered by
 *  Survey Loop" mark. Mirrors what respondents see in the public page
 *  so editor previews stay 1:1 with production. */
export function SurveyBrandFooter({ organization }: SurveyBrandFooterProps) {
  const orgName = organizationDisplayName(organization);
  const address = (organization?.orgAddress ?? "").trim();
  const city = (organization?.orgCity ?? "").trim();
  const state = (organization?.orgState ?? "").trim();
  const zip = (organization?.orgZip ?? "").trim();
  const email = organization?.orgEmail ?? "";
  const phone = organization?.orgPhone ?? "";
  const location = [address, city, [state, zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  const hasLocation = Boolean(location);
  const hasContact = Boolean(email || phone);

  return (
    <footer className={styles.footer}>
      {organization ? (
        <>
          {orgName ? (
            <div className={styles.org}>{orgName}</div>
          ) : null}
          {hasLocation ? (
            <div className={styles.contact}>
              {location}
            </div>
          ) : null}
          {hasContact ? (
            <div className={styles.contact}>
              {email} {phone}
            </div>
          ) : null}
        </>
      ) : null}
      <a
        className={styles.brand}
        href="https://surveyloop.io"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span>Powered by</span>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="https://hosting.surveyloop.io/logo-dark.png"
          alt="Survey Loop"
        />
      </a>
    </footer>
  );
}
